import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Overlay, usePopover } from "@gemini/core";
import { Button, Tabs } from "@gemini/ui";
import { CloseIcon, GearFilledIcon } from "@gemini/icons";

import { RouterTabPanel } from "./panels/RouterTabPanel";
import { ApplicationTabPanel } from "./panels/ApplicationTabPanel";
import { DebugTabPanel } from "./panels/DebugTabPanel";
import { useDragging } from "./useDragging";

export const DevTools = () => (
  <Overlay isOpen>
    <DevToolsContent />
  </Overlay>
);

const DevToolsContent = () => {
  const [currentTab, setCurrentTab] = useState(RouterTabPanel.id);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hasDragged = useDragging({ ref: triggerRef, active: !isOpen });

  const tabsItems = useMemo(() => {
    return [
      {
        id: RouterTabPanel.id,
        label: RouterTabPanel.label,
        content: (
          <RouterTabPanel.Component
            isActive={isOpen && currentTab === RouterTabPanel.id}
          />
        ),
      },
      {
        id: ApplicationTabPanel.id,
        label: ApplicationTabPanel.label,
        content: (
          <ApplicationTabPanel.Component
            isActive={isOpen && currentTab === ApplicationTabPanel.id}
          />
        ),
      },
      {
        id: DebugTabPanel.id,
        label: DebugTabPanel.label,
        content: <DebugTabPanel.Component />,
      },
    ];
  }, [currentTab, isOpen]);

  const { boxProps } = usePopover(
    { trigger: triggerRef, popover: popoverRef },
    {
      close() {
        // No-op to require an explicit action to close the dev tools to prevent auto-closing behavior.
        // Why? Prevent developer frustration especially when the scroll target is delegated from the dev tools to the body
        // (it can occur automatically when an inner scrollable popover container reaches its last item)
      },
      isOpen,
      offset: 20,
      placement: "top",
      unsafe_isNonModal: true,
    },
  );

  // Closing behavior
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    const handleClickOutside = (event: Event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("pointerdown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("pointerdown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <Box
      position="fixed"
      bottom="spacing.8"
      left="spacing.8"
      zIndex="zIndex.2"
    >
      <Box
        {...boxProps.popover}
        ref={popoverRef}
        boxShadow="shadow.8"
        borderRadius="radius.8"
        overflow="hidden"
        opacity={isOpen ? "opacity.full" : "opacity.none"}
        visibility={isOpen ? "visible" : "hidden"}
        transitionProperty="opacity, transform, visibility"
        transitionDuration="motionDuration.300"
        transform={isOpen ? undefined : "translateY(10%)"}
        backgroundColor="color.background.default"
        width="sizing.536"
        maxWidth="90vw"
      >
        <Tabs
          items={tabsItems}
          defaultValue={currentTab}
          onChange={setCurrentTab}
        />
      </Box>
      <Button
        ref={triggerRef}
        icon={isOpen ? CloseIcon : GearFilledIcon}
        variant="primary"
        onPress={() => {
          if (hasDragged.current) return;

          setIsOpen(!isOpen);
        }}
      >
        Dev Tools
      </Button>
    </Box>
  );
};
